import apiClient from './api';

export type SocialPlatform = 'instagram' | 'facebook' | 'whatsapp';

export interface SocialIntegrationConfig {
  platform: SocialPlatform;
  configured: boolean;
}

export interface SocialAccount {
  _id: string;
  platform: SocialPlatform;
  externalAccountId: string;
  accountName: string;
  accountHandle?: string;
  accountImageUrl?: string;
  isActive: boolean;
  capabilities: string[];
  needsReconnect: boolean;
  lastAuthError?: string;
}

export type SocialSourceType = 'clip' | 'upload' | 'text';
export type SocialContentType = 'video' | 'post' | 'message';

export interface SocialPublishRequest {
  platform: SocialPlatform;
  accountId: string;
  sourceType: SocialSourceType;
  sourceId?: string;
  contentType: SocialContentType;
  caption?: string;
  hashtags?: string[];
  recipient?: string;
  scheduledFor?: string;
  idempotencyKey?: string;
  useOrgBranding?: boolean;
  applyFadeOut?: boolean;
}

export interface SocialPublishJob {
  jobId: string;
  status: 'pending' | 'scheduled' | 'processing' | 'completed' | 'failed' | 'cancelled';
  externalPostId?: string;
  postUrl?: string;
  error?: string;
  scheduledFor?: string;
}

export const socialApi = {
  getConfig: async (): Promise<{ success: boolean; platforms: SocialIntegrationConfig[] }> => {
    const response = await apiClient.get('/social/config');
    return response.data;
  },
  getAuthUrl: async (): Promise<{ success: boolean; url: string }> => {
    const response = await apiClient.get('/social/meta/auth-url');
    return response.data;
  },
  getAccounts: async (platform?: SocialPlatform): Promise<{ success: boolean; accounts: SocialAccount[] }> => {
    const response = await apiClient.get('/social/accounts', { params: platform ? { platform } : undefined });
    return response.data;
  },
  unlinkAccount: async (accountId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/social/accounts/${accountId}`);
    return response.data;
  },
  publish: async (payload: SocialPublishRequest): Promise<{ success: boolean; publishJobId: string; message: string }> => {
    const response = await apiClient.post('/social/publish', payload);
    return response.data;
  },
  getPublishJob: async (jobId: string): Promise<{ success: boolean; job: SocialPublishJob }> => {
    const response = await apiClient.get(`/social/publish-jobs/${jobId}`);
    return response.data;
  },
  reschedulePublishJob: async (jobId: string, scheduledFor: string): Promise<{ success: boolean; job?: SocialPublishJob }> => {
    const response = await apiClient.patch(`/social/publish-jobs/${jobId}`, { scheduledFor });
    return response.data;
  },
  cancelPublishJob: async (jobId: string): Promise<{ success: boolean; job?: SocialPublishJob }> => {
    const response = await apiClient.patch(`/social/publish-jobs/${jobId}`, { cancel: true });
    return response.data;
  },
};
